import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import getSymbolFromCurrency from 'currency-symbol-map';
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { RiShoppingBagLine } from "react-icons/ri";
import hero1 from "../Assest/images/hero1.jpg";
import hero2 from "../Assest/images/hero2.jpg";
import hero3 from "../Assest/images/hero3.jpg";
import hero4 from "../Assest/images/hero4.jpg";
import Card from "../components/Card";
import Video from "../components/Video";
import GallerySlider from "../components/GallerySlider";
import Perfume from "../components/Perfume";
import Customer from "../components/Customer";
import SubscribeNewsletter from "../components/SubscribeNewsletter";
import Branding from "./Branding";
import Logo from "../Assest/images/logo.png";
import LoadingScreen from "./LoadingScreen";
import Loader from "../components/Loader";
import axios from "axios";
import { useCurrency } from "../components/CurrencyContext";

// Fallback slides in case API fails
const fallbackSlides = [
  {
    id: 1,
    images: { front: hero1, back: hero2 },
    tagline: "SIGNATURE COLLECTION",
    title: "Royal Oud",
    brandId: 1,
    subtitle: "Premium Luxury Fragrance",
    description:
      "Experience the epitome of luxury with our signature collection.",
    price: "$299",
    details: "Made with premium Arabian oud and rare essential oils.",
  },
  {
    id: 2,
    images: { front: hero3, back: hero4 },
    tagline: "EXCLUSIVE SERIES",
    title: "Desert Rose",
    subtitle: "Limited Edition Series",
    brandId: 2,
    description:
      "Discover the rare blend of precious middle eastern ingredients.",
    price: "$349",
    details: "Crafted with Bulgarian rose and exotic spices.",
  },
  {
    id: 3,
    images: { front: hero2, back: hero1 },
    tagline: "ARTISAN BLEND",
    title: "Amber Musk",
    brandId: 3,
    subtitle: "Prestige Collection",
    description:
      "A sophisticated blend that captivates with its warm, exotic aura.",
    price: "$279",
    details: "Infused with rare amber and oriental musk notes.",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isProductLoading, setIsProductLoading] = useState(false);
  const [apiData, setApiData] = useState({
    homepage: [],
    gallery: [],
    collection: [],
    fragrance: [],
  });
  const [slides, setSlides] = useState(fallbackSlides);
  const [dataFetched, setDataFetched] = useState(false);
  const companyId = 65 // prefix compnay id vital Source

  const { rate, currency } = useCurrency();

  const convertPrice = (price) => {
    const productPrice = parseFloat(price) || 0;
    const convertedPrice = productPrice * rate;
    return convertedPrice
  }

  // Get the correct currency symbol (with special case for SAR)
  const currencySymbol = currency === "SAR" ? "﷼" : getSymbolFromCurrency(currency) || currency;


  useEffect(() => {
    // Only hide loading screen when data is actually fetched
    if (dataFetched) {
      const loadingTimer = setTimeout(() => {
        setIsLoading(false);
      }, 800); // Just a short delay after data is fetched

      const contentTimer = setTimeout(() => {
        setContentVisible(true);
      }, 1200);

      return () => {
        clearTimeout(loadingTimer);
        clearTimeout(contentTimer);
      };
    }
  }, [dataFetched]);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if we have cached data and it's not expired
        const cachedData = localStorage.getItem("homepageData");
        const cacheTimestamp = localStorage.getItem("homepageDataTimestamp");
        const now = new Date().getTime();

        // Use cache if it exists and is less than 1 hour old
        if (
          cachedData &&
          cacheTimestamp &&
          now - parseInt(cacheTimestamp) < 3600000
        ) {
          const parsedData = JSON.parse(cachedData);
          setApiData(parsedData);

          // Transform homepage data for slides if available
          if (parsedData.homepage.length > 0) {
            const transformedSlides = parsedData.homepage.map((item) => {
              // Your existing transformation logic
              const images = JSON.parse(item.product_images || "[]");
              return {
                id: item.company_product_id,
                images: {
                  front: `${process.env.REACT_APP_API_URI}/uploads/${images[0] || ""
                    }`,
                  back: `${process.env.REACT_APP_API_URI}/uploads/${images[1] || images[0] || ""
                    }`,
                },
                tagline: item.product_type.toUpperCase(),
                title: item.product_name,
                brandId: item.brand_id,
                subtitle: item.brand_name || "Premium Luxury Fragrance",
                description:
                  item.description ||
                  "Experience the epitome of luxury with our signature collection.",
                price: parseFloat(item.price).toFixed(2),
                currency_code: item.currency_code,
                details:
                  item.description ||
                  "Made with premium ingredients and rare essential oils.",
              };
            });
            setSlides(transformedSlides);
          }

          setDataFetched(true);
          console.log("Using cached data");
          return;
        }

        // If no valid cache, fetch from API
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/products/structure?company_id=${companyId}`
        );
        if (response.status === 200) {
          const data = response.data;

          // Process and set the data (your existing code)
          const processedData = {
            homepage: data.find((item) => item.homepage)?.homepage || [],
            gallery: data.find((item) => item.gallery)?.gallery || [],
            collection: data.find((item) => item.collection)?.collection || [],
            fragrance: data.find((item) => item.fragrance)?.fragrance || [],
          };

          setApiData(processedData);

          // Save to cache with timestamp
          localStorage.setItem("homepageData", JSON.stringify(processedData));
          localStorage.setItem(
            "homepageDataTimestamp",
            new Date().getTime().toString()
          );

          // Transform homepage data for slides
          if (processedData.homepage.length > 0) {
            // Your existing transformation logic
            const transformedSlides = processedData.homepage.map((item) => {
              const images = JSON.parse(item.product_images || "[]");
              return {
                // Your existing object structure
                id: item.company_product_id,
                images: {
                  front: `${process.env.REACT_APP_API_URI}/uploads/${images[0] || ""
                    }`,
                  back: `${process.env.REACT_APP_API_URI}/uploads/${images[1] || images[0] || ""
                    }`,
                },
                tagline: item.product_type.toUpperCase(),
                title: item.product_name,
                brandId: item.brand_id,
                subtitle: item.company_name || "Premium Luxury Fragrance",
                description:
                  item.description ||
                  "Experience the epitome of luxury with our signature collection.",
                price: parseFloat(item.price).toFixed(2),
                currency_code: item.currency_code,
                details:
                  item.description ||
                  "Made with premium ingredients and rare essential oils.",
              };
            });
            setSlides(transformedSlides);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // On error, use fallback slides
        setSlides(fallbackSlides);
      } finally {
        setDataFetched(true);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);

    // Only start the loading timers after data is fetched
    if (dataFetched) {
      const loadingTimer = setTimeout(() => {
        setIsLoading(false);
      }, 2800);

      const contentTimer = setTimeout(() => {
        setContentVisible(true);
      }, 3200);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        clearTimeout(loadingTimer);
        clearTimeout(contentTimer);
      };
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [dataFetched]);

  // Swipe functionality handlers
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 100) {
      // Swipe left - go to next slide
      changeSlide("next");
    }

    if (touchStart - touchEnd < -100) {
      // Swipe right - go to previous slide
      changeSlide("prev");
    }
  };

  const handleShopNow = useCallback(
    async (brandId, brandName) => {
      try {
        setIsProductLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/products/brand/${brandId}`
        );
        if (response.status === 200) {
          localStorage.setItem("apiProducts", JSON.stringify(response.data));

          navigate(`/product?brand=${brandName}&id=${brandId}`, {
            state: {
              apiData: response.data,
              brandId: brandId,
            },
          });
        }
      } catch (error) {
        console.error("Error fetching company ", error);
      } finally {
        setIsProductLoading(false);
      }
    },
    [navigate]
  );

  const changeSlide = (direction) => {
    setIsFlipped(true);
    setTimeout(() => {
      setCurrentSlide((prev) =>
        direction === "next"
          ? (prev + 1) % slides.length
          : (prev - 1 + slides.length) % slides.length
      );
      setIsFlipped(false);
    }, 300);
  };


  return (
    <>
      <AnimatePresence mode="wait">
        {isLoading && <LoadingScreen />}
      </AnimatePresence>

      <motion.div
        className="relative min-h-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 2.8 }}
      >
        {/* Dynamic Background with improved gradients */}
        {isProductLoading && <Loader Logo={Logo} />}
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-white to-orange-50" />
          <motion.div
            className="absolute blur-3xl opacity-30 bg-gradient-to-r from-orange-200 to-rose-200 rounded-full w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 transition-transform duration-1000"
            style={{
              transform: `translate(${mousePosition.x * 0.05}px, ${mousePosition.y * 0.05
                }px)`,
            }}
          />
          <motion.div
            className="absolute bottom-0 right-0 blur-3xl opacity-20 bg-gradient-to-l from-purple-100 to-rose-200 rounded-full w-64 md:w-96 h-64 md:h-96 transition-transform duration-1000"
            style={{
              transform: `translate(${mousePosition.x * -0.03}px, ${mousePosition.y * -0.03
                }px)`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, #00000005 1px, transparent 1px),
                linear-gradient(to bottom, #00000005 1px, transparent 1px)
              `,
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        {/* Main Content with better centering */}
        <AnimatePresence>
          {contentVisible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center w-full"
            >
              {/* Hero Section */}
              <section className="relative min-h-screen w-full overflow-hidden pt-20 pb-5 sm:pt-4 sm:pb-0">
                <div className="relative container mx-auto px-4 sm:px-6 pt-16 sm:pt-20 md:pt-28 lg:pt-32 pb-10 md:pb-20">
                  {/* Mobile-first approach - Card first, then content */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mx-auto max-w-7xl">
                    {/* Card Section - Shows at top on mobile */}
                    <div className="order-1 lg:order-2 relative flex justify-center mx-auto w-full">
                      <motion.div
                        className="relative w-full max-w-xs sm:max-w-sm md:max-w-md perspective mx-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                      >
                        {/* 3D Card Stack Effect - Responsive sizing */}
                        <div className="hidden sm:block absolute inset-0 -left-8 sm:-left-12 md:-left-16 top-4 sm:top-6 md:top-8 w-[250px] sm:w-[300px] md:w-[350px] h-[380px] sm:h-[420px] md:h-[500px] bg-gradient-to-br from-gray-100 to-orange-50 rounded-2xl transform rotate-[-15deg] shadow-xl" />
                        <div className="hidden sm:block absolute inset-0 -right-8 sm:-right-12 md:-right-16 top-4 sm:top-6 md:top-8 w-[250px] sm:w-[300px] md:w-[350px] h-[380px] sm:h-[420px] md:h-[500px] bg-gradient-to-br from-orange-50 to-rose-50 rounded-2xl transform rotate-[15deg] shadow-xl" />

                        {/* Main Flipping Card - Responsive sizing */}
                        <motion.div
                          className="relative w-[250px] sm:w-[280px] md:w-[330px] lg:w-[350px] h-[360px] sm:h-[400px] md:h-[470px] lg:h-[500px] mx-auto perspective"
                          animate={{ rotateY: isFlipped ? 180 : 0 }}
                          transition={{ duration: 0.7, ease: "easeInOut" }}
                          style={{ transformStyle: "preserve-3d" }}
                        >
                          {/* Front Card */}
                          <div
                            className="absolute inset-0 w-full h-full backface-hidden cursor-pointer"
                            onClick={() => setIsFlipped(true)}
                            style={{ backfaceVisibility: "hidden" }}
                          >
                            <div className="relative w-full h-full group">
                              <motion.img
                                src={slides[currentSlide].images.front}
                                alt="Product Front"
                                className="w-full h-full object-cover rounded-2xl shadow-2xl"
                                whileHover={{ scale: 1.02 }}
                                transition={{ duration: 0.3 }}
                              />
                              {/* Enhanced Glass Overlay */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 right-6 sm:right-8 text-white transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                  <h3 className="text-xl sm:text-2xl font-light mb-2">
                                    Click for Details
                                  </h3>
                                  <div className="h-[1px] w-16 bg-white/50" />
                                  <motion.div
                                    className="mt-2 text-sm text-white/80"
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                  >
                                    Swipe to explore more
                                  </motion.div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Back Card with improved design */}
                          <div
                            className="absolute inset-0 w-full h-full backface-hidden cursor-pointer"
                            onClick={() => setIsFlipped(false)}
                            style={{
                              backfaceVisibility: "hidden",
                              transform: "rotateY(180deg)",
                            }}
                          >
                            <div className="relative w-full h-full group">
                              <motion.img
                                src={slides[currentSlide].images.back}
                                alt="Product Back"
                                className="w-full h-full object-cover rounded-2xl shadow-2xl"
                                whileHover={{ scale: 1.02 }}
                                transition={{ duration: 0.3 }}
                              />
                              {/* Enhanced Content Overlay */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-2xl backdrop-blur-sm">
                                <div className="absolute inset-0 p-4 sm:p-6 md:p-8 text-white flex flex-col justify-end">
                                  <motion.span
                                    className="tracking-[0.2em] text-xs text-orange-300 mb-1"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                  >
                                    {slides[currentSlide].tagline}
                                  </motion.span>
                                  <motion.h3
                                    className="text-2xl sm:text-3xl font-light mb-2 sm:mb-4"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                  >
                                    {slides[currentSlide].title}
                                  </motion.h3>
                                  {/* <motion.p
                                    className="text-base sm:text-lg text-white/90 mb-2 sm:mb-4"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                  >
                                    {slides[currentSlide].details}
                                  </motion.p> */}
                                  <motion.div
                                    className="flex items-center space-x-2 sm:space-x-4"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                  >
                                    <span className="text-xl sm:text-2xl font-light whitespace-nowrap">
                                      {currencySymbol} {convertPrice(slides[currentSlide]?.price).toFixed(2)}
                                    </span>

                                    <div className="h-[1px] w-10 sm:w-16 bg-orange-400/40" />
                                    <span className="text-sm sm:text-base text-white/70">
                                      Limited Edition
                                    </span>
                                  </motion.div>
                                  <div className="flex gap-3 mt-4 sm:mt-6">
                                    <motion.button
                                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-400 to-rose-400 rounded-full text-xs sm:text-sm 
                                                transition-all duration-300"
                                      whileHover={{ scale: 1.05 }}
                                      onClick={() =>
                                        handleShopNow(
                                          slides[currentSlide].brandId, slides[currentSlide].subtitle
                                        )
                                      }
                                      whileTap={{ scale: 0.95 }}
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: 0.5 }}
                                    >
                                      Shop Now
                                    </motion.button>
                                    <motion.button
                                      className="px-4 sm:px-6 py-2 sm:py-3 bg-white/20 backdrop-blur-sm rounded-full text-xs sm:text-sm 
                                                hover:bg-white/30 transition-all duration-300"
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: 0.6 }}
                                    >
                                      View Collection
                                    </motion.button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>

                        {/* Enhanced Floating Elements */}
                        <motion.div
                          className="absolute -right-10 sm:-right-16 md:-right-20 top-10 w-16 sm:w-24 md:w-32 h-16 sm:h-24 md:h-32 bg-orange-200/40 rounded-full blur-2xl"
                          animate={{
                            y: [0, 20, 0],
                            opacity: [0.3, 0.7, 0.3],
                          }}
                          transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                        <motion.div
                          className="absolute -left-10 sm:-left-16 md:-left-20 bottom-10 w-20 sm:w-32 md:w-40 h-20 sm:h-32 md:h-40 bg-rose-200/40 rounded-full blur-2xl"
                          animate={{
                            y: [0, -20, 0],
                            opacity: [0.3, 0.7, 0.3],
                          }}
                          transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 1,
                          }}
                        />
                        <motion.div
                          className="absolute bottom-1/2 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-32 h-32 bg-white/30 rounded-full blur-3xl"
                          animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.1, 0.2, 0.1],
                          }}
                          transition={{
                            duration: 6,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      </motion.div>
                    </div>

                    {/* Content Section - Shows below card on mobile */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="order-2 lg:order-1 space-y-6 sm:space-y-8 mt-8 lg:mt-0 text-center lg:text-left"
                    >
                      {/* Tagline with animated line */}
                      <div className="flex items-center space-x-4 justify-center lg:justify-start">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: 48 }}
                          className="h-[1px] bg-gradient-to-r from-orange-400 to-rose-400"
                        />
                        <span className="text-orange-500 tracking-[0.2em] sm:tracking-[0.3em] text-xs sm:text-sm font-medium">
                          {slides[currentSlide].tagline}
                        </span>
                      </div>

                      {/* Title & Subtitle with improved animation */}
                      <motion.div
                        className="space-y-1 sm:space-y-2"
                        key={currentSlide} // This forces re-animation when slide changes
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-gray-900">
                          {slides[currentSlide].title}
                        </h1>
                        <p className="text-lg sm:text-xl text-gray-600">
                          {slides[currentSlide].subtitle}
                        </p>
                      </motion.div>

                      {/* Description & Price with improved animation */}
                      <motion.div
                        className="space-y-4 sm:space-y-6"
                        key={`desc-${currentSlide}`} // This forces re-animation when slide changes
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        {/* <p className="text-base sm:text-lg text-gray-600 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                          {slides[currentSlide].description}
                        </p> */}
                        <div className="text-3xl sm:text-4xl font-light text-gray-900">
                          {currencySymbol} {convertPrice(slides[currentSlide]?.price).toFixed(2)}
                        </div>
                      </motion.div>

                      {/* Buttons with enhanced design */}
                      <motion.div
                        className="flex flex-wrap gap-4 justify-center lg:justify-start pt-4 sm:pt-8"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <motion.button
                          onClick={() =>
                            handleShopNow(slides[currentSlide].brandId, slides[currentSlide].subtitle)
                          }
                          className="group relative overflow-hidden px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-full shadow-lg"
                          whileHover={{
                            scale: 1.05,
                            boxShadow:
                              "0 10px 25px -5px rgba(249, 115, 22, 0.4)",
                          }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <motion.span className="absolute inset-0 w-full h-full bg-gradient-to-r from-rose-500 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <span className="relative z-10 flex items-center space-x-2">
                            <RiShoppingBagLine
                              size={18}
                              className="sm:text-lg"
                            />
                            <span className="text-sm sm:text-base">
                              Shop Now
                            </span>
                          </span>
                        </motion.button>
                        <motion.button
                          className="px-6 sm:px-8 py-3 sm:py-4 border border-orange-200 text-gray-800 text-sm sm:text-base rounded-full hover:bg-orange-50 transition-colors duration-300 shadow-md hover:shadow-lg"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Learn More
                        </motion.button>
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* Navigation Controls - Improved design and interaction */}
                  <div className="absolute -bottom-4 sm:bottom-4 md:bottom-8 lg:bottom-12 left-1/2 transform -translate-x-1/2 flex items-center space-x-6 sm:space-x-12">
                    <motion.button
                      onClick={() => changeSlide("prev")}
                      className="group p-2 sm:p-3 md:p-4 rounded-full bg-white/90 backdrop-blur shadow-lg hover:bg-gradient-to-r hover:from-orange-500 hover:to-rose-500 hover:text-white transition-all duration-300"
                      whileHover={{
                        scale: 1.1,
                        boxShadow: "0 10px 25px -5px rgba(249, 115, 22, 0.4)",
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FiArrowLeft className="text-lg sm:text-xl group-hover:text-white" />
                    </motion.button>

                    <div className="flex space-x-2 sm:space-x-4">
                      {slides.map((_, index) => (
                        <motion.button
                          key={index}
                          onClick={() => {
                            setIsFlipped(true);
                            setTimeout(() => {
                              setCurrentSlide(index);
                              setIsFlipped(false);
                            }, 300);
                          }}
                          className={`w-12 sm:w-20 h-1 sm:h-1.5 rounded-full transition-all duration-300 ${currentSlide === index
                            ? "bg-gradient-to-r from-orange-500 to-rose-500 w-16 sm:w-24 md:w-32"
                            : "bg-gray-300 hover:bg-gray-400"
                            }`}
                          whileHover={{ scale: 1.1, height: "8px" }}
                        />
                      ))}
                    </div>

                    <motion.button
                      onClick={() => changeSlide("next")}
                      className="group p-2 sm:p-3 md:p-4 rounded-full bg-white/90 backdrop-blur shadow-lg hover:bg-gradient-to-r hover:from-orange-500 hover:to-rose-500 hover:text-white transition-all duration-300"
                      whileHover={{
                        scale: 1.1,
                        boxShadow: "0 10px 25px -5px rgba(249, 115, 22, 0.4)",
                      }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FiArrowRight className="text-lg sm:text-xl group-hover:text-white" />
                    </motion.button>
                  </div>
                </div>
              </section>

              {/* Additional Sections with improved animation and spacing */}
              <div className="w-full space-y-16 sm:space-y-24 md:space-y-32 py-16 sm:py-24 md:py-32">
                {[
                  { Component: Branding, data: apiData.gallery },
                  { Component: Card, data: {} },
                  { Component: Video, data: {} },
                  { Component: GallerySlider, data: apiData.collection },
                  { Component: Perfume, data: apiData.fragrance },
                  { Component: Customer, data: {} },
                  { Component: SubscribeNewsletter, data: {} },
                ].map(({ Component, data }, index) => (
                  <motion.section
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="container mx-auto px-4 sm:px-6"
                  >
                    <Component data={data} />
                  </motion.section>
                ))}
              </div>

              {/* Elegant Footer */}
              <motion.footer
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="w-full bg-gradient-to-r from-orange-50 to-rose-50 py-12 px-4"
              >
                <div className="container mx-auto flex flex-col items-center">
                  <img src={Logo} alt="vital Source Logo" className="h-10 mb-4" />
                  <div className="h-[1px] w-24 bg-gradient-to-r from-orange-400 to-rose-400 mb-8" />
                  <div className="text-sm text-gray-600 text-center">
                    {/* © {new Date().getFullYear()} vital Source Fragrances. All rights reserved. */}{" "}
                    #Vital Soruce
                    <br />
                    <p className="text-gray-400 text-sm mt-4 max-w-xl mx-auto italic">
                      Discover our massive selection of home appliances, kitchen essentials, and daily staples, carefully curated to fulfill all your household needs under one roof.
                    </p>
                  </div>
                </div>
              </motion.footer>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default Home;
